import React from "react"
import PropTypes from "prop-types"
import { parseISO, format as formatDate } from "date-fns"

/**
 * Format currency for display
 */
function formatCurrency(value) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "$0"
  }
  return `$${Math.round(value).toLocaleString()}`
}

/**
 * Format hours for display
 */
function formatHours(value) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "0h"
  }
  return `${value.toFixed(1)}h`
}

/**
 * Format booking date in user's local timezone
 */
function formatBookingDate(bookingDate) {
  if (!bookingDate) return ""
  
  try {
    const date = parseISO(bookingDate)
    // Display in user's local timezone
    return formatDate(date, "EEE, MMM d")
  } catch {
    return bookingDate
  }
}

/**
 * Format booking time in user's local timezone
 */
function formatBookingTime(bookingTime) {
  if (!bookingTime) return ""
  
  try {
    // If it's already in HH:MM format, return as is
    if (/^\d{1,2}:\d{2}$/.test(bookingTime)) {
      return bookingTime
    }
    
    // Otherwise parse as ISO timestamp and convert to local time
    const date = parseISO(bookingTime)
    return formatDate(date, "h:mm a")
  } catch {
    return bookingTime
  }
}

/**
 * JobSuggestionItem displays an individual job service suggestion
 *
 * @param {Object} props
 * @param {Object} props.suggestion - Job service suggestion data
 * @param {number} props.rank - Priority rank (1-10)
 * @param {Function} props.onSelect - Callback when item is selected
 * @param {boolean} props.isDisabled - Whether the item is disabled
 */
export default function JobSuggestionItem({
  suggestion,
  rank,
  onSelect,
  isDisabled
}) {
  const {
    id,
    job_no: jobNo,
    address,
    booking_date: bookingDate,
    booking_time: bookingTime,
    estimated_hours: estimatedHours,
    price,
    product_name: productName,
    value_per_hour: valuePerHour,
    fit_score: fitScore
  } = suggestion

  const handleClick = () => {
    if (!isDisabled && onSelect) {
      onSelect(suggestion)
    }
  }

  return (
    <div
      className={`job-suggestion-item ${isDisabled ? "disabled" : ""}`}
      onClick={handleClick}
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      onKeyPress={e => {
        if (e.key === "Enter" && !isDisabled) {
          handleClick()
        }
      }}>
      {/* Rank Badge */}
      <div className="suggestion-rank">
        <span className="rank-badge">{rank}</span>
      </div>

      {/* Job Details */}
      <div className="suggestion-details">
        <div className="suggestion-header">
          <span className="job-number">{jobNo || `Job #${id}`}</span>
          {productName && (
            <span className="product-name">{productName}</span>
          )}
        </div>

        <div className="suggestion-address">
          <i className="fas fa-map-marker-alt mr-2" />
          {address}
        </div>

        <div className="suggestion-meta">
          <span className="meta-item booking-date">
            <i className="fas fa-calendar-alt mr-1" />
            {formatBookingDate(bookingDate)}
          </span>
          <span className="meta-item">
            <i className="fas fa-clock mr-1" />
            {formatBookingTime(bookingTime)}
          </span>
          <span className="meta-item">
            <i className="fas fa-hourglass-half mr-1" />
            {formatHours(estimatedHours)}
          </span>
          <span className="meta-item">
            <i className="fas fa-dollar-sign mr-1" />
            {formatCurrency(price)}
          </span>
        </div>

        {/* Additional metrics */}
        <div className="suggestion-metrics">
          <span className="metric-badge" title="Value per hour">
            {formatCurrency(valuePerHour)}
            /h
          </span>
          <span className="metric-badge" title="Remaining capacity after assignment">
            {formatHours(fitScore)}
            {" "}
            remaining
          </span>
        </div>
      </div>

      {/* Action Icon */}
      <div className="suggestion-action">
        <i className="fas fa-plus-circle" />
      </div>
    </div>
  )
}

JobSuggestionItem.propTypes = {
  suggestion: PropTypes.shape({
    id: PropTypes.number.isRequired,
    job_no: PropTypes.string,
    address: PropTypes.string,
    booking_date: PropTypes.string,
    booking_time: PropTypes.string,
    estimated_hours: PropTypes.number,
    price: PropTypes.number,
    product_name: PropTypes.string,
    value_per_hour: PropTypes.number,
    fit_score: PropTypes.number
  }).isRequired,
  rank: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool
}

JobSuggestionItem.defaultProps = {
  isDisabled: false
}
